import { ChatChannelWithParticipant } from '@core/swagger-client';
import { ChatChannelIds, Participant } from '@core/types';
import type { WorkBook } from 'xlsx';

export interface ChatExportRow {
    time: string;
    sender: string;
    emailAddress: string;
    message: string;
}

// Same fallback as the server's DisplayName(): on-demand viewers can chat without a nickname
export const getParticipantName = (participant: Participant) =>
    participant.nickname || `${participant.firstName ?? ''} ${participant.lastName ?? ''}`.trim() || participant.emailAddress;

export const getChannelName = (channel: ChatChannelWithParticipant) => {
    switch (channel.channelId) {
        case ChatChannelIds.allChannel:
            return 'All chat';
        case ChatChannelIds.moderatorChannel:
            return 'Moderator chat';
        case ChatChannelIds.presenterChannel:
            return 'Presenter chat';
        case ChatChannelIds.publicChannel:
            return 'Public chat';
        default:
            return channel.participant ? getParticipantName(channel.participant) : '';
    }
};

export const buildChatExportRows =(channels: ChatChannelWithParticipant[], channelId: string, participants: Participant[]): ChatExportRow[] => {
    const emailAddresses = new Map(participants.map((participant) => [participant.participantId, participant.emailAddress]));
    const selected = channelId === ChatChannelIds.allChannel ? channels : channels.filter((channel) => channel.channelId === channelId);

    return selected
        .flatMap((channel) => channel.messages)
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .map((message) => ({
            // Same as the page's Time column
            time: new Date(message.timestamp).toLocaleTimeString(),
            sender: message.senderNickname,
            emailAddress: emailAddresses.get(message.senderParticipantId) ?? '',
            message: message.message
        }));
};

export const buildChatWorkbook = (xlsx: typeof import('xlsx'), rows: ChatExportRow[], webinarTitle: string, channel: string): WorkBook => {
    const workbook = xlsx.utils.book_new();
    workbook.Props = {
        Application: 'OnlineWebinar',
        AppVersion: '1.0.0',
        Author: 'OnlineWebinar',
        CreatedDate: new Date(),
        Title: 'Chat messages for ' + webinarTitle
    };

    // prettier-ignore
    const sheet = xlsx.utils.aoa_to_sheet([
        ['Chat messages for ' + webinarTitle],
        [],
        ['Exported on', , , , new Date()],
        ['Channel', , , , channel],
        [],
        ['Time', 'Sender', 'Email address', 'Message'],
        ...rows.map((row) => [row.time, row.sender, row.emailAddress, row.message])
    ]);
    xlsx.utils.book_append_sheet(workbook, sheet, 'Chat messages');

    return workbook;
};
